/**
 * Estimates list (tracker item 39): newest first, filtered by status and a
 * search (number, title, customer, address), with New estimate. Office roles
 * only (the route and the nav item check canManageEstimates).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createAPIClient, IEstimatesApi } from '../../api';
import * as types from '../../api/types';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { getErrorMessage } from '../../utils/apiError';
import { invoiceDate, invoiceMoney } from '../invoiceUi';
import { ESTIMATE_STATUS_LABELS, estimateAddress, estimateBundleNames, estimatePath, estimateStatusClass } from './estimateUi';

const PAGE_SIZE = 20;

export function EstimateListPage() {
  const api = useMemo<IEstimatesApi>(() => createAPIClient(), []);
  const navigate = useNavigate();
  const [status, setStatus] = useState<types.EstimateStatus | ''>('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<types.EstimatePage<types.EstimateSummary> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const data = await api.listEstimates({ status, search: query, page, limit: PAGE_SIZE });
      if (id === requestId.current) setResult(data);
    } catch (cause) {
      if (id === requestId.current) setError(getErrorMessage(cause, 'Could not load estimates'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [api, status, query, page]);

  useEffect(() => { void load(); }, [load]);

  const rows = result?.data ?? [];
  const pages = result?.pagination.pages ?? 1;

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Estimates</h1>
        <button type="button" className={primaryButtonClass} onClick={() => navigate('/estimates/new')}>New estimate</button>
      </div>

      <form
        className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[12rem_1fr_auto] sm:items-end"
        onSubmit={(event) => { event.preventDefault(); setPage(1); setQuery(search.trim()); }}
      >
        <div>
          <label htmlFor="estimate-status" className={labelClass}>Status</label>
          <select id="estimate-status" className={inputClass} value={status} onChange={(e) => { setPage(1); setStatus(e.target.value as types.EstimateStatus | ''); }}>
            <option value="">All</option>
            {types.ESTIMATE_STATUSES.map((s) => <option key={s} value={s}>{ESTIMATE_STATUS_LABELS[s]}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="estimate-search" className={labelClass}>Search</label>
          <input id="estimate-search" className={inputClass} value={search} placeholder="Number, title, customer or address" onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button type="submit" className={secondaryButtonClass}>Search</button>
      </form>

      {error && <p role="alert" className="mt-4 rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      {loading && !result && <p role="status" className="mt-4 text-gray-600">Loading estimates…</p>}

      {result && rows.length === 0 && !loading && (
        <p className="mt-6 rounded-lg bg-white p-6 text-center text-sm text-gray-600 shadow">
          {status || query ? 'No estimates match these filters.' : 'No estimates yet. Create one to quote a customer.'}
        </p>
      )}

      {rows.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-lg bg-white shadow">
          <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm" aria-label="Estimates">
            <thead className="bg-gray-50">
              <tr>
                {['Number', 'Customer', 'Property', 'Title', 'Status', 'Expires', 'Total'].map((h) => (
                  <th key={h} scope="col" className={`px-4 py-3 font-semibold text-gray-700 ${h === 'Total' ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((e) => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold" data-label="Number" data-primary>
                    <Link to={estimatePath(e.id)} className="text-blue-700 hover:underline">{e.estimateNumber}</Link>
                  </td>
                  <td className="px-4 py-3 break-words" data-label="Customer">{e.customer.name}</td>
                  <td className="px-4 py-3 break-words" data-label="Property">{estimateAddress(e.property)}</td>
                  <td className="px-4 py-3 break-words" data-label="Title">
                    {e.title ?? '—'}
                    {estimateBundleNames(e).length > 0 && (
                      <span
                        className="ml-2 inline-block rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-900"
                        data-testid="estimate-bundle-badge" title={estimateBundleNames(e).join(', ')}
                      >
                        {estimateBundleNames(e).length === 1 ? `Bundle: ${estimateBundleNames(e)[0]}` : `${estimateBundleNames(e).length} bundles`}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3" data-label="Status">
                    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${estimateStatusClass(e.status)}`}>{ESTIMATE_STATUS_LABELS[e.status]}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" data-label="Expires">{invoiceDate(e.expiryDate)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap font-semibold" data-label="Total">{invoiceMoney(e.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-between gap-3 text-sm">
          <button type="button" className={secondaryButtonClass} disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span>Page {page} of {pages}</span>
          <button type="button" className={secondaryButtonClass} disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </main>
  );
}
